import axios from 'axios';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_URL } from '../../../http/http-config';

const UGC_TopicModal = forwardRef((props, ref) => {
    const [modalBox, setModalBox] = useState(false);
    const [subjects, setSubjects] = useState([]);
    const [sections, setSections] = useState([]);
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [filteredSections, setFilteredSections] = useState([]);

    const initialData = {
        ExamType: 'Select Section Type',
        Topic: 'N/A',
        Section: '',
        SectionID: 0,
        Subject: '',
        SubjectID: 0,
        Sn: 0,
        DisplayOrder: 0
    };

    const [formData, setFormData] = useState(initialData);
    console.log(formData);

    useImperativeHandle(ref, () => ({
        openModal(item = {}) {
            if (item.Sn > 0) {
                setFormData({ ...item, DisplayOrder: item.TopicOrder || 0 });
            }
            setModalBox(true);
        }
    }));

    // Fetch subjects & sections
    useEffect(() => {
        axios.get(BACKEND_SERVER_URL + '/ugc/subjects')
            .then((res) => setSubjects(res.data.subjects))
            .catch(() => toast.error('Failed to fetch subjects'));

        axios.get(BACKEND_SERVER_URL + '/ugc/sections')
            .then((res) => setSections(res.data.sections))
            .catch(() => toast.error('Failed to fetch sections'));
        return (() => {
            setSubjects([]);
            setSections([]);
            setSelectedSubjects([]);
            setFilteredSections([]);
            setFormData(initialData);
        });
    }, []);

    // Filter sections when subject changes
    useEffect(() => {
        if (formData.SubjectID) {
            const filteredSubjects = subjects.filter(sub => sub.ExamType === formData.ExamType);
            setSelectedSubjects(filteredSubjects);
            const filtered = sections?.filter(sec => sec.SubjectID === formData.SubjectID);
            console.log(filtered);
            setFilteredSections(filtered);
        } else {
            setFilteredSections([]);
        }
    }, [formData.SubjectID, sections, subjects]);

    const handleSave = async () => {
        if (!formData.SubjectID) {
            toast.error("Please select Subject");
            return;
        }
        if (!formData.SectionID) {
            toast.error("Please select Topics");
            return;
        }
        if (!formData.Topic.trim()) {
            toast.error("Please enter Sub Topics");
            return;
        }

        try {
            const url = !formData.Sn ? '/ugc/createTopic' : '/ugc/updateTopic';
            const response = await axios.post(BACKEND_SERVER_URL + url, formData);
            toast.success(response.data.message);
            props.setSubjectLoading(!props.loadSubjects);
            setModalBox(false);
            setFormData(initialData);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Error saving topic");
        }
    };

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            width: '100%',
            backgroundColor: 'transparent',
            borderColor: '#17a2b8',
        }),
    };

    return (
        <Modal onHide={() => setModalBox(false)} show={modalBox} size="md" backdrop="static" keyboard={false} enforceFocus={false}>
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">
                        {formData.Sn ? "Update Sub Topics" : "Add Sub Topics"}
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setModalBox(false)}></button>
                </div>
                <div className="modal-body">
                    {/* Subject Dropdown */}
                    <div className="mb-3">
                        <label className="form-label text-primary">SECTION TYPE<span className="required">*</span></label>
                        <Select
                            value={{
                                value: formData.ExamType,
                                label: formData.ExamType === 'GENERAL APTITUDE' ? 'Teaching & Research Aptitude' : formData.ExamType === 'SUBJECT SPECIFIC' ? 'Academic Subjects' : formData.ExamType
                            }}
                            onChange={(selectedOption) => {
                                setFormData({ ...formData, ExamType: selectedOption.value });
                                setSelectedSubjects(subjects.filter((subject) => subject.ExamType === selectedOption.value));
                                if (selectedOption.value != 'GENERAL APTITUDE') {
                                    setFormData((preData) => ({ ...preData, Topic: '' }));
                                }
                            }}
                            options={[
                                { value: 'SUBJECT SPECIFIC', label: 'Academic Subjects' },
                                { value: 'GENERAL APTITUDE', label: 'Teaching & Research Aptitude' }
                            ]}
                            styles={selectStyles}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-primary">SUBJECT NAME<span className="required">*</span></label>
                        <Select
                            value={formData.SubjectID ? { value: formData.SubjectID, label: formData.Subject } : null}
                            onChange={(opt) => setFormData({ ...formData, Subject: opt.label, SubjectID: opt.value.Sn, SectionID: 0, Section: '' })}
                            options={[...(selectedSubjects || [])].sort((a,b) => a.Subject?.localeCompare(b.Subject)).map(s => ({ value: s, label: s.Subject }))}
                            styles={selectStyles}
                            placeholder="Select Subject"
                        // isClearable
                        />
                    </div>

                    {/* Section Dropdown */}
                    <div className="mb-3">
                        <label className="form-label text-primary">TOPICS<span className="required">*</span></label>
                        <Select
                            value={formData.SectionID ? { value: formData.SectionID, label: formData.Section } : null}
                            onChange={(opt) => setFormData({ ...formData, Section: opt.label, SectionID: opt.value.Sn })}
                            options={[...(filteredSections || [])].sort((a,b) => a.Section?.localeCompare(b.Section)).map(s => ({ value: s, label: s.Section }))}
                            styles={selectStyles}
                            placeholder="Select Topics"
                            // isClearable
                            isDisabled={!formData.SubjectID}
                        />
                    </div>

                    {/* Topic Name */}
                    <div className="mb-3">
                        <label className="form-label text-primary">SUB TOPICS<span className="required">*</span></label>
                        <input
                            autoFocus
                            className='form-control'
                            type="text"
                            value={formData.Topic}
                            onChange={(e) => setFormData({ ...formData, Topic: e.target.value })}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="displayOrder" className="form-label text-primary">ORDER PREFERENCE</label>
                        <input className='form-control' type="number" min="0" id="displayOrder" value={formData.DisplayOrder} onChange={(e) => setFormData({ ...formData, DisplayOrder: e.target.value })} placeholder="e.g. 1, 2, 3... (0 for alphabetical)" />
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-danger btn-xs" onClick={() => setModalBox(false)}>Close</button>
                    <button type="button" className="btn btn-primary btn-xs" onClick={handleSave}>
                        {formData.Sn ? "Update" : "Add"}
                    </button>
                </div>
            </div>
        </Modal>
    );
});

export default UGC_TopicModal;
